import { ReactNode, memo, useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import { Formik, FormikConfig, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import { Form, Input, Radio } from 'antd';
import { UploadFile, UploadFileStatus } from 'antd/lib/upload/interface';
import styled from '@emotion/styled';

import FormField from 'components/FormField';
import ImageUploader from './ImageUploader';

export interface EnterpriseFormValues extends Omit<Oversea.Enterprise, 'image'> {
  image?: UploadFile[];
}

type FV = EnterpriseFormValues;

export type EnterpriseFormHelpers = FormikHelpers<FV>;

type FormRenderChildren<FV> = (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface EnterpriseFormProps extends Omit<FormikConfig<FV>, 'initialValues'> {
  initialValues?: FV;
  mode?: 'read' | 'modify';
  children?: ReactNode | FormRenderChildren<FV>;
}

const defaultInitialValues: FV = {
  type: 0,
  name: '',
  code: '',
};

const Img = styled.img`
  width: 120px;
  height: auto;
`;
const Flex = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0 18px;
  > * {
    width: 28%;
  }
`;

function FormContent(props: { children?: ReactNode } & Pick<EnterpriseFormProps, 'mode'>) {
  const { children = null, mode = 'read' } = props;
  const isEditing = mode === 'modify';

  const { t } = useTranslate();

  const {
    values: { type },
  } = useFormikContext<FV>();

  return (
    <Form layout="vertical">
      <Flex>
        <FormField<FV['type']> name="type" required={isEditing} label={t('enterprise.认证类型')}>
          {({ field }) => {
            const { value, onChange } = field;
            const text = value === 0 ? t('enterprise.企业') : t('enterprise.个人');
            return isEditing ? (
              <Radio.Group value={value} onChange={({ target: { value } }) => onChange(value)}>
                <Radio.Button value={0}>{t('enterprise.企业')}</Radio.Button>
                <Radio.Button value={1}>{t('enterprise.个人')}</Radio.Button>
              </Radio.Group>
            ) : (
              text
            );
          }}
        </FormField>
        <FormField<FV['name']>
          name="name"
          required={isEditing}
          label={
            {
              0: t('enterprise.企业名称'),
              1: t('enterprise.姓名'),
            }[type]
          }
        >
          {({ field }) => {
            return isEditing ? (
              <Input
                placeholder={
                  {
                    0: t('enterprise.请输入企业名称'),
                    1: t('enterprise.请输入真实姓名'),
                  }[type]
                }
                {...field}
              />
            ) : (
              field.value || '-'
            );
          }}
        </FormField>
        <FormField<FV['code']>
          name="code"
          required={isEditing}
          label={
            {
              0: t('enterprise.社会信用代码'),
              1: t('enterprise.身份证'),
            }[type]
          }
        >
          {({ field }) => {
            return isEditing ? (
              <Input
                placeholder={
                  {
                    0: t('enterprise.请输入社会信用代码'),
                    1: t('enterprise.请输入身份证号码'),
                  }[type]
                }
                {...field}
              />
            ) : (
              field.value || '-'
            );
          }}
        </FormField>
        {type === 0 && (
          <>
            <FormField<FV['bank']> name="bank" label={t('enterprise.开户行')}>
              {({ field }) => {
                return isEditing ? (
                  <Input placeholder={t('enterprise.请输入开户行')} {...field} />
                ) : (
                  field.value || '-'
                );
              }}
            </FormField>
            <FormField<FV['address']> name="address" label={t('enterprise.注册地址')}>
              {({ field }) => {
                return isEditing ? (
                  <Input placeholder={t('enterprise.请输入注册地址')} {...field} />
                ) : (
                  field.value || '-'
                );
              }}
            </FormField>
            <FormField<FV['phone']> name="phone" label={t('enterprise.注册电话')}>
              {({ field }) => {
                return isEditing ? (
                  <Input placeholder={t('enterprise.请输入注册电话')} {...field} />
                ) : (
                  field.value || '-'
                );
              }}
            </FormField>
            <FormField<FV['image']> name="image" label={t('enterprise.营业执照')}>
              {({ field }) => {
                const { value, onChange } = field;
                const [{ url: imgSrc } = {} as any] = value || [{}];
                return isEditing ? (
                  <ImageUploader
                    fileList={value}
                    onChange={({ fileList }) => {
                      onChange(
                        fileList.map((file) => {
                          const {
                            response: {
                              data: { url: responseUrl } = {
                                url: undefined,
                              },
                            } = {},
                            url = responseUrl,
                          } = file;

                          return {
                            ...file,
                            url,
                          };
                        }),
                      );
                    }}
                  />
                ) : (
                  imgSrc && <Img alt={t('enterprise.营业执照')} src={imgSrc} />
                );
              }}
            </FormField>
          </>
        )}
      </Flex>
      {children}
    </Form>
  );
}

function EnterpriseForm(props: EnterpriseFormProps) {
  const { initialValues = defaultInitialValues, children = null, mode, ...otherProps } = props;

  const { t } = useTranslate();

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof FV, string>> = {};

      const { type, name, code, image } = values;

      if (!name || name.trim().length <= 1) {
        errors.name = {
          0: t('enterprise.请输入正确的企业名称'),
          1: t('enterprise.请输入正确的姓名'),
        }[type];
      }

      if (!code || !code.trim()) {
        errors.code = {
          0: t('enterprise.请输入社会信用代码'),
          1: t('enterprise.请输入身份证号码'),
        }[type];
      } else if (type === 0 && (code.length !== 18 || !/^[A-Za-z0-9]+$/.test(code))) {
        errors.code = t('enterprise.请输入正确的社会信用代码');
      } else if (type === 1 && (code.length !== 18 || !/^[xX0-9]+$/.test(code))) {
        errors.code = t('enterprise.请输入正确的身份证号码');
      }

      const invalidStatusList: UploadFileStatus[] = ['error', 'uploading', 'removed'];

      if (image && image[0] && image[0].status && invalidStatusList.includes(image[0].status)) {
        errors.image = t('enterprise.图片未成功上传');
      }

      return errors;
    },
    [t],
  );
  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validate={validate}
      {...otherProps}
    >
      {(props) => {
        if (children && typeof children === 'function') {
          return (children as FormRenderChildren<FV>)(<FormContent mode={mode} />, props);
        }

        return <FormContent mode={mode}>{children}</FormContent>;
      }}
    </Formik>
  );
}

export default memo(EnterpriseForm);
